import { SmartColumn } from "@modules/spreadsheets/dto/spreadsheet/type";
import { SPREADSHEET_TABLE_CONSTANTS } from "../table.types";
import { SpreadsheetType } from "@modules/spreadsheets/dto/spreadsheet/type";
import { SPREADSHEET_FIELD_CONTEXT } from "@modules/spreadsheets/const/common";

/**
 * Checks if a column is the row count column
 * @param column - The column to check (SmartColumn or any object with field property)
 * @returns true if the column is a row count column
 */
export const isRowCountColumn = (column: SmartColumn | { field?: { fieldid?: number; id?: number } }): boolean => {
  const fieldId = column?.field?.fieldid ?? (column as any)?.field?.id;
  return Number(fieldId) === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID;
};

/**
 * Creates a row count column with the proper structure
 * @param spreadsheetType - The type of spreadsheet (individual or pedigree) - can be string or SpreadsheetType enum
 * @returns A SmartColumn object representing the row count column
 */
export const createRowCountColumn = (spreadsheetType: SpreadsheetType | string): SmartColumn => {
  return {
    width: SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH,
    sortOrder: 0,
    sortAsc: true,
    visible: false,
    editable: false,
    columnCount: 1,
    columnOrder: -1,
    field: {
      imageURL: "gif/sysheadpeg.gif",
      fieldid: SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID,
      fieldname: "Row Count",
      permissions: "WRITE",
      tableIndex: 0,
      defaultWidth: SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE,
      fieldClass: "java.lang.Integer",
      system: true,
      systemDataField: false,
      required: false,
      fieldnameWithPath: undefined,
      fieldcontext:  SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL,
      dataSourceNodeId: 0,
      type: 3,
      fieldtype: {
        typeid: 3,
        typename: "Number",
        computed: false,
        systemType: false,
      },
    },
  };
};

/**
 * Ensures that a row count column exists in the columns array.
 * If it doesn't exist, adds it at the beginning of the array.
 * @param columns - Array of SmartColumn objects
 * @param spreadsheetType - The type of spreadsheet (individual or pedigree) - can be string or SpreadsheetType enum
 * @returns Array of SmartColumn objects with row count column guaranteed to exist
 */
export const ensureRowCountColumn = (
  columns: SmartColumn[],
  spreadsheetType: SpreadsheetType | string
): SmartColumn[] => {
  if (!Array.isArray(columns)) {
    return columns;
  }
  
  if (columns.some(isRowCountColumn)) {
    return columns;
  }
  
  return [createRowCountColumn(spreadsheetType), ...columns];
};

/**
 * Filters out row count columns from an array of columns
 * @param columns - Array of SmartColumn objects
 * @returns Array of SmartColumn objects without row count columns
 */
export const filterOutRowCountColumns = (columns: SmartColumn[]): SmartColumn[] => {
  if (!Array.isArray(columns)) {
    return columns;
  }
  return columns.filter((col) => !isRowCountColumn(col));
};

